/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, Modal, Tooltip } from 'antd';
import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { ApiError, CreateInstallerTokenResponse } from '@/client';
import { ValidationError } from '@/components/Forms/ValidationError';
import { validationErrors } from '@/components/Forms/validationErrors';
import { useMessage } from '@/hooks/message';
import { useCreateInstallerTokenMutation } from '@/hooks/mutation/useCreateInstallerTokenMutation';
import { usePaginatedNodeGroupsQuery } from '@/hooks/query/usePaginatedNodeGroupsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Input } from '@/primitives/Input';
import { Select } from '@/primitives/Select';
import { Text } from '@/primitives/Text';
import { getValidationMessage } from '@/utils/errors';
import './CreateInstallerTokenModal.css';

type CreateInstallerTokenModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: (data: CreateInstallerTokenResponse) => void;
};

type FormFields = {
  name: string;
  nodeGroupId?: string;
};

export function CreateInstallerTokenModal({ open, onClose, onSuccess }: CreateInstallerTokenModalProps) {
  const { t } = useMimicTranslation('installerTokens');
  const [form] = Form.useForm<FormFields>();
  const [message] = useMessage();
  const [isLoading, setIsLoading] = useState(false);
  const [fleetSearchValue, setFleetSearchValue] = useState('');
  const [fleetSearchInput, setFleetSearchInput] = useState('');

  const nodeGroupsQuery = usePaginatedNodeGroupsQuery({
    params: {
      pageSize: 100,
      filters: fleetSearchValue ? { name: fleetSearchValue } : undefined,
    },
    type: 'inMemory',
  });

  const fleetOptions =
    nodeGroupsQuery.data?.data.map((group) => ({
      label: group.name,
      value: group.id,
    })) || [];

  const debouncedFleetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFleetSearchValue(value);
      }, 300),
    [],
  );

  const handleFleetSearch = useCallback(
    (value: string) => {
      setFleetSearchInput(value);
      debouncedFleetSearch(value);
    },
    [debouncedFleetSearch],
  );

  const handleFleetSelect = useCallback(() => {
    debouncedFleetSearch.cancel();
    setFleetSearchInput('');
    setFleetSearchValue('');
  }, [debouncedFleetSearch]);

  const { mutate: createTokenMutation } = useCreateInstallerTokenMutation({
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        form.setFields(validationErrors<FormFields>({ name: t('duplicateTokenName') }));
      } else {
        const errorMessage = getValidationMessage(error, 'name');
        if (errorMessage) {
          form.setFields(validationErrors<FormFields>({ name: errorMessage }));
        } else {
          message.error(t('creationError'));
        }
      }
      setIsLoading(false);
    },
    onSuccess: (data) => {
      setIsLoading(false);
      form.resetFields();
      onClose();
      onSuccess(data);
    },
    onMutate: () => {
      setIsLoading(true);
    },
  });

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      createTokenMutation({ name: values.name, nodeGroupId: values.nodeGroupId });
    } catch (error) {
      // Validation failed, errors are already shown in the form
    }
  };

  const handleCancel = () => {
    debouncedFleetSearch.cancel();
    form.resetFields();
    setFleetSearchInput('');
    setFleetSearchValue('');
    onClose();
  };

  return (
    <Modal
      title={t('createModalTitle')}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={isLoading}
      okButtonProps={{ disabled: isLoading }}
      cancelButtonProps={{ disabled: isLoading }}
      okText={t('generate')}
      centered
    >
      <div style={{ marginBottom: '1rem', marginTop: '1rem' }}>
        <Text>{t('createModalDescription')}</Text>
      </div>
      <Form form={form} layout="vertical">
        <Form.Item
          label={
            <span>
              {t('tokenNameLabel')}{' '}
              <Tooltip title={t('tokenNameTooltip')} overlayClassName="token-name-tooltip">
                <InfoCircleOutlined style={{ cursor: 'pointer', color: '#8c8c8c' }} />
              </Tooltip>
            </span>
          }
          name="name"
          rules={[
            {
              required: true,
              message: <ValidationError>{t('tokenNameRequired')}</ValidationError>,
            },
            {
              max: 100,
              message: <ValidationError>{t('tokenNameMaxLength', { maxLength: 100 })}</ValidationError>,
            },
          ]}
        >
          <Input placeholder={t('tokenNamePlaceholder')} disabled={isLoading} autoFocus />
        </Form.Item>
        <Form.Item
          label={
            <span>
              {t('fleetLabel')}{' '}
              <Tooltip title={t('fleetTooltip')} overlayClassName="token-name-tooltip">
                <InfoCircleOutlined style={{ cursor: 'pointer', color: '#8c8c8c' }} />
              </Tooltip>
            </span>
          }
          name="nodeGroupId"
        >
          <Select
            placeholder={t('fleetPlaceholder')}
            options={fleetOptions}
            allowClear
            showSearch
            searchValue={fleetSearchInput}
            onSearch={handleFleetSearch}
            onChange={(value) => {
              if (value) {
                handleFleetSelect();
              }
            }}
            onClear={handleFleetSelect}
            filterOption={false}
            notFoundContent={nodeGroupsQuery.isLoading ? t('fleetLoading') : t('fleetNotFound')}
            disabled={isLoading}
            data-testid="fleet-select"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
